import React, { useState, useEffect, useLayoutEffect } from "react";
import { useStyles } from "./ReceiptLookupPage.styles";
import clsx from "clsx";
import AuthService from "../../services/AuthService";
import ReceiptPortalService, {
  ReceiptPortalStatus,
} from "../../services/ReceiptPortalService";
import apiClient, { setAuthentication as setApiAuth } from "../../auth/apiClient";
import { setAuthenticationState } from "../../reducers/auth/authReducer";
import useAuthContext from "../../hooks/useAuthContext";
import { Grid, CircularProgress, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import GenericError from "../../components/MobilePayment/GenericError";
import { useParams } from "react-router-dom";
import ReceiptLookupForm from "./ReceiptLookupForm";

const ONE_YEAR_NOTICE =
  "Looking for a receipt? Receipts can be accessed through this portal for up to 1 year after the transaction date. If your transaction occurred more than 1 year ago, the receipt will no longer be available online.";

const ReceiptLookupPage = () => {
  const authService = new AuthService(apiClient);
  const receiptPortalService = new ReceiptPortalService(apiClient);
  const classes = useStyles();
  const theme = useTheme();
  const { entityId } = useParams();
  const { authReducer } = useAuthContext();
  const [, authDispatch] = authReducer;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [isContextLoaded, setIsContextLoaded] = useState(false);

  const getAuth = async () => {
    try {
      var appClientToken = await authService.getMobilePayAuthToken();
      if (appClientToken !== "") {
        setApiAuth(appClientToken);
        authDispatch({
          type: setAuthenticationState,
          payload: { properties: [] },
        });
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error("Error Authenticating", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getContext = async () => {
    try {
      const resolved = await receiptPortalService.getContext(entityId);
      setContext(resolved);
    } finally {
      setIsContextLoaded(true);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      getAuth();
    }
  }, [authDispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      getContext();
    }
  }, [isAuthenticated, entityId]);

  useLayoutEffect(() => {
    document.body.style.backgroundColor = theme.palette.background.level1;
  }, []);

  const entityName = context?.name;
  const lprEnabled = context?.lprEnabled === true;
  const status = context?.status;

  const resolveView = () => {
    if (isLoading || (isAuthenticated && !isContextLoaded)) {
      return "loading";
    }
    if (!isAuthenticated) {
      return "auth-error";
    }
    if (status === ReceiptPortalStatus.Available) {
      return "form";
    }
    return "unavailable";
  };

  const renderContent = (view) => {
    switch (view) {
      case "loading":
        return <CircularProgress size={60} />;
      case "auth-error":
        return <GenericError message="Please try again later." />;
      case "unavailable":
        return (
          <GenericError
            headerMessage="Receipt lookup unavailable"
            message="This receipt lookup is not available. Please check the link and try again."
          />
        );
      case "form":
        return (
          <div className={classes.formContainer}>
            <div className={classes.header}>
              <Typography
                data-id="receipt-entity-name"
                variant="h6"
                className={classes.entityName}
              >
                {entityName}
              </Typography>
              <Typography
                data-id="receipt-lookup-title"
                variant="h5"
                color="primary"
                className={classes.title}
              >
                Receipt Lookup
              </Typography>
              <Typography variant="body2" className={classes.notice}>
                {ONE_YEAR_NOTICE}
              </Typography>
            </div>
            <ReceiptLookupForm
              entityId={entityId}
              lprEnabled={lprEnabled}
              isContextLoaded={isContextLoaded}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const view = resolveView();

  return (
    <div className={clsx(classes.root)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <img
            className={classes.logo}
            alt="company logo"
            src="/images/logos/a1-logo-stacked-dark.svg"
          />
        </Grid>
      </Grid>
      <div className={clsx(classes.contentContainer, classes.loadingSpinner)}>
        {renderContent(view)}
      </div>
    </div>
  );
};

export default ReceiptLookupPage;
